import { ChatInput } from '@/components/conversations/conversation-chat/ChatInput';
import { ChatMessage } from '@/components/conversations/conversation-chat/ChatMessage';
import { ConversationEventRow } from '@/components/conversations/conversation-chat/ConversationEventRow';
import { ConversationHeader } from '@/components/conversations/conversation-chat/ConversationHeader';
import { ConversationSummaryPanel } from '@/components/conversations/conversation-chat/ConversationSummaryPanel';
import { MessagesSeparator } from '@/components/ui/MessagesSeparator';
import { useI18n } from '@/context/I18nProvider';
import { formatPhoneNumber, cn } from '@/lib/utils';
import { getStoryMediaUrl } from '@/lib/profile-pictures';
import socketClient from '@/services/socket-client';
import { ApiClient } from '@/services/api-client';
import { IChannel, ITriggerType } from '@/services/types';
import { Classification, ConversationData, ConversationEvent, MessageEntry, MessageStatus } from '@/services/types/Lead.types';
import { fDate } from '@/utils/format-time';
import { CircularLoader } from '@/components/ui/CircularLoader';
import { languageDisplayName, useConversationTranslation } from '@/hooks/useConversationTranslation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
type ConversationChatProps = {
  conversationData: ConversationData | undefined;
  isLoading: boolean;
  deselectConversation?: () => void;
  isMobile?: boolean;
};
const ConversationChat = ({
  conversationData,
  isLoading,
  isMobile,
  deselectConversation = () => {}
}: ConversationChatProps) => {
  const {
    t,
    locale
  } = useI18n();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<MessageEntry[]>(conversationData?.ConversationHistory || []);
  const [sortedUpcomingMessages, setSortedUpcomingMessages] = useState<MessageEntry[]>([]);
  const [isTriggeringAi, setIsTriggeringAi] = useState(false);
  // Set once the user has asked the AI to respond, to prevent firing a second
  // forced response while the first is still being generated (which would
  // produce a duplicate reply). Reset when the conversation changes or a new
  // inbound message arrives.
  const [hasTriggeredAi, setHasTriggeredAi] = useState(false);

  // Messages the salesperson reads: the history and the AI replies waiting to go out.
  const translatableMessages = useMemo(() => [...messages, ...sortedUpcomingMessages].filter(message => !message.IsDeleted && !message.ContentUnavailable && message.Content).map(message => ({
    text: message.Content,
    inbound: !!message.Inbound
  })), [messages, sortedUpcomingMessages]);
  const translation = useConversationTranslation({
    leadId: conversationData?.LeadInformation?.LeadID ?? '',
    leadLanguage: conversationData?.LeadLanguage,
    messages: translatableMessages
  });
  const leadLanguageName = languageDisplayName(conversationData?.LeadLanguage, locale);
  const isWaitingAiResponse = conversationData?.Classification === Classification.WAITING_AI_RESPONSE;

  // The last message in the (live) history — used to decide whether the AI
  // still owes a reply to the lead.
  const lastMessageIsInbound = messages.length > 0 && !!messages[messages.length - 1]?.Inbound;

  // Surface the "ask AI to respond" trigger whenever the AI is enabled, the
  // lead sent the latest message (so a reply is owed), and no AI response is
  // already scheduled (no upcoming messages waiting to be sent).
  //
  // Gate on the *live* `lastMessageIsInbound` rather than `isWaitingAiResponse`:
  // the latter is derived from the cached `conversationData.Classification`,
  // which is NOT refreshed after we trigger a response, so it stays "4" forever
  // and would keep the banner pinned open even once the AI has replied. Once the
  // AI's reply lands (an outbound message becomes the last message), the banner
  // hides on its own.
  const canTriggerAiResponse = !!conversationData?.IsActive && sortedUpcomingMessages.length === 0 && lastMessageIsInbound;
  const handleTriggerAiResponse = async () => {
    if (!conversationData?.LeadInformation?.LeadID) return;
    if (isTriggeringAi || hasTriggeredAi) return;
    setIsTriggeringAi(true);
    try {
      await ApiClient.lead.triggerAiResponse({
        leadId: conversationData.LeadInformation.LeadID
      });
      // Lock the trigger so a second click can't queue a duplicate response
      // while the AI is generating its reply.
      setHasTriggeredAi(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      toast.success((t as any)('conversations.triggerAiResponseSuccess'));
      queryClient.invalidateQueries({
        queryKey: ['lead.waitingAiResponseCount']
      });
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      toast.error((t as any)('conversations.triggerAiResponseError'));
    } finally {
      setIsTriggeringAi(false);
    }
  };

  // Create a ref to hold the conversation history for mutations
  const conversationHistoryRef = useRef(conversationData?.ConversationHistory);
  const isChatInputDisabled = useMemo(() => {
    // Personal WhatsApp (QR code) has no 24h messaging window restriction
    if (conversationData?.LeadInformation?.IsPersonalWhatsapp) return false;
    const inboundMessages = conversationData?.ConversationHistory.filter(msg => msg.Inbound).sort((a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime());
    const lastLeadMessage = inboundMessages?.[0] || null;

    // Disabled if lead has never answered or last answer was > 24h ago
    if (!lastLeadMessage) return true;
    return Date.now() - new Date(lastLeadMessage.Timestamp).getTime() > 24 * 60 * 60 * 1000;
  }, [conversationData?.ConversationHistory, conversationData?.LeadInformation?.IsPersonalWhatsapp]);

  // Timeline = messages + system events (follow-ups that never went out),
  // each event pinned at the instant its message would have been sent so the
  // gap it left is visible exactly where it is.
  type TimelineItem = {
    kind: 'message';
    at: number;
    message: MessageEntry;
  } | {
    kind: 'event';
    at: number;
    event: ConversationEvent;
  };
  const events = useMemo(() => conversationData?.Events ?? [], [conversationData?.Events]);
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = messages.map(message => ({
      kind: 'message',
      at: new Date(message.Timestamp).getTime(),
      message
    }));
    for (const event of events) {
      items.push({
        kind: 'event',
        at: new Date(event.Timestamp).getTime(),
        event
      });
    }
    return items.sort((a, b) => a.at - b.at);
  }, [messages, events]);

  // An event is obsolete once anything was said after the decision was taken:
  // the conversation moved on, the missing follow-up no longer matters.
  const isEventObsolete = (event: ConversationEvent) => {
    const decidedAt = new Date(event.CreatedAt).getTime();
    return messages.some(message => new Date(message.Timestamp).getTime() > decidedAt);
  };
  // Only the latest live event carries the nudge button, and only while the
  // AI can actually send something.
  const nudgeableEventId = useMemo(() => {
    if (sortedUpcomingMessages.length > 0) return null;
    const live = events.filter(event => !isEventObsolete(event));
    return live.length > 0 ? live[live.length - 1].EventID : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, messages, sortedUpcomingMessages.length]);
  const nudgeDisabledReason: 'aiDisabled' | 'windowClosed' | null = !conversationData?.IsActive ? 'aiDisabled' : isChatInputDisabled ? 'windowClosed' : null;

  // ------------------ EFFECTS ---------------------------------------

  useEffect(() => {
    if (conversationData?.UpcomingMessages) {
      const sorted = [...conversationData.UpcomingMessages].sort((a, b) => new Date(a.Timestamp).getTime() - new Date(b.Timestamp).getTime());
      setSortedUpcomingMessages(sorted);
    } else setSortedUpcomingMessages([]);
  }, [conversationData?.UpcomingMessages]);

  // Update messages when conversation data changes
  useEffect(() => {
    setMessages(conversationData?.ConversationHistory || []);
    conversationHistoryRef.current = conversationData?.ConversationHistory;
    // Delay scrolling slightly to ensure content is rendered
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  }, [conversationData]);

  // Reset the AI-trigger lock whenever we switch to a different conversation.
  useEffect(() => {
    setHasTriggeredAi(false);
  }, [conversationData?.LeadInformation?.LeadID]);

  // Set up socket listeners with leadId verification
  useEffect(() => {
    let isMounted = true;
    const setupSocketListeners = async () => {
      const socket = await socketClient.getSocket();
      if (socket && isMounted) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleSocketMessage = (data: any, inbound: boolean) => {
          const parsedData = JSON.parse(data);

          // Verify that the message belongs to this conversation
          if (parsedData.leadId !== conversationData?.LeadInformation.LeadID) {
            return; // Skip messages not meant for this conversation
          }
          const newEntry: MessageEntry = {
            Content: parsedData.message,
            Inbound: inbound,
            Timestamp: new Date().toISOString(),
            ExternalMessageId: parsedData.externalMessageId,
            IsDeleted: false,
            ...(inbound ? {} : {
              Status: MessageStatus.SENDING
            }),
            ...(!inbound && parsedData.sentBy ? {
              SentBy: parsedData.sentBy
            } : {}),
            ...(parsedData.buttons ? {
              Buttons: parsedData.buttons.map((b: {
                title: string;
                url: string;
              }) => ({
                Title: b.title,
                URL: b.url
              }))
            } : {}),
            ...(parsedData.quick_replies ? {
              QuickReplies: parsedData.quick_replies.map((qr: {
                title: string;
                payload: string;
              }) => ({
                Title: qr.title,
                Payload: qr.payload
              }))
            } : {})
          };
          if (isMounted) {
            setMessages(prevMessages => {
              // If this confirms a message we already added optimistically (manual
              // send or a resend), update that entry in place instead of appending
              // a duplicate bubble.
              const clientMessageId = parsedData.clientMessageId;
              if (clientMessageId) {
                const existingIndex = prevMessages.findIndex(msg => msg.ClientMessageId === clientMessageId);
                if (existingIndex !== -1) {
                  const updated = [...prevMessages];
                  updated[existingIndex] = {
                    ...updated[existingIndex],
                    ...newEntry
                  };
                  return updated;
                }
              }
              return [...prevMessages, newEntry];
            });
            // Update the conversation history through the ref
            conversationHistoryRef.current?.push(newEntry);
            setSortedUpcomingMessages([]);
            // Release the AI-trigger lock as soon as any new message lands:
            // an outbound message means the AI fulfilled the forced response
            // (so the "Réponse demandée…" button must reset), and an inbound
            // message means the lead said something new the AI can respond to.
            setHasTriggeredAi(false);
            // Delay scrolling slightly to ensure new message is rendered
            setTimeout(() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                  top: scrollContainerRef.current.scrollHeight,
                  behavior: 'smooth'
                });
              }
            }, 100);
          }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleOutboundMessageErr = (data: any) => {
          const clientMessageId = data?.clientMessageId;
          const errorMessage = typeof data === 'object' && data !== null ? data.error || t('common.error') : String(data || t('common.error'));
          if (clientMessageId && isMounted) {
            let matched = false;
            setMessages(prevMessages => prevMessages.map(msg => {
              if (msg.ClientMessageId !== clientMessageId) return msg;
              matched = true;
              return {
                ...msg,
                Status: MessageStatus.FAILED,
                StatusError: errorMessage
              };
            }));
            if (matched) return;
          }

          // No matching in-flight message found (e.g. an error unrelated to a
          // specific send) — fall back to a toast so the failure isn't silent.
          toast.error(t('instagram.messageError'), {
            description: errorMessage
          });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleSocketMessageUpdate = (data: any) => {
          const parsedData = JSON.parse(data);
          if (parsedData.leadId !== conversationData?.LeadInformation.LeadID) return;
          if (isMounted) {
            setMessages(prevMessages => prevMessages.filter(msg => !(msg.ExternalMessageId === parsedData.externalMessageId && parsedData.deleted)));
            conversationHistoryRef.current = conversationHistoryRef.current?.filter(msg => !(msg.ExternalMessageId === parsedData.externalMessageId && parsedData.deleted));
          }
        };
        socket.on('inbound_message_received', data => handleSocketMessage(data, true));
        socket.on('outbound_message_sent', data => handleSocketMessage(data, false));
        socket.on('message_updated', data => handleSocketMessageUpdate(data));
        socket.on('outbound_message_err', data => handleOutboundMessageErr(data));
        socket.onAny((event, ...args) => {
          // outbound_message_err is handled by the dedicated listener above
          // (it correlates to a specific message bubble via clientMessageId).
          if (event.endsWith('_err') && event !== 'outbound_message_err') {
            const errorObj = args[0];
            const errorMessage = typeof errorObj === 'object' && errorObj !== null ? errorObj.error || t('common.error') : String(errorObj || t('common.error'));
            toast.error(t('instagram.messageError'), {
              description: errorMessage
            });
          }
        });
      }
    };
    setupSocketListeners().catch(() => {
      toast.warning(t('errors.manualMessagingUnavailable'));
    });
    return () => {
      isMounted = false;
      socketClient.getSocket().then(socket => {
        if (socket) {
          socket.off('inbound_message_received');
          socket.off('outbound_message_sent');
          socket.removeAllListeners();
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationData?.LeadInformation.LeadID]); // Add leadId to dependencies

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      const scrollHeight = scrollContainerRef.current.scrollHeight;
      scrollContainerRef.current.scrollTo(0, scrollHeight);
    }
  };
  const handleSendMessage = (text: string, clientMessageId: string) => {
    const optimisticEntry: MessageEntry = {
      Content: text,
      Inbound: false,
      Timestamp: new Date().toISOString(),
      Status: MessageStatus.SENDING,
      SentBy: 'user',
      ClientMessageId: clientMessageId
    };
    setMessages(prevMessages => [...prevMessages, optimisticEntry]);
    scrollToBottom();
  };
  const handleResend = (message: MessageEntry) => {
    if (!conversationData) return;
    const newClientMessageId = crypto.randomUUID();
    setMessages(prevMessages => prevMessages.map(msg => msg.ClientMessageId === message.ClientMessageId ? {
      ...msg,
      Status: MessageStatus.SENDING,
      StatusError: undefined,
      ClientMessageId: newClientMessageId
    } : msg));
    socketClient.sendMessage(conversationData.LeadInformation.OrgId, conversationData.LeadInformation.LeadID, message.Content, undefined, newClientMessageId);
  };
  if (isLoading) {
    return <div className="flex-1 h-full flex items-center justify-center">
        <CircularLoader />
      </div>;
  }
  if (!conversationData) {
    return <div className="flex flex-1 items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">{t('conversations.noConversationSelected')}</p>
      </div>;
  }
  return <div className={`flex flex-1 h-full w-full min-w-0 flex-col overflow-x-hidden ${isMobile ? 'pb-1' : 'pb-4'}`}>
      <ConversationHeader id={conversationData.LeadInformation.LeadID} name={`${conversationData.LeadInformation.FirstName}${conversationData.LeadInformation.LastName ? ` ${conversationData.LeadInformation.LastName}` : ''}`} channel={conversationData.Channel} contactIdentifier={conversationData.LeadInformation?.Username || formatPhoneNumber(conversationData.LeadInformation.PhoneNumber)} isActive={conversationData.IsActive} keepAgentOnManualReply={conversationData.KeepAgentOnManualReply} classification={conversationData.Classification} profilePictureURL={conversationData.LeadInformation.ProfilePictureURL} isMobile={isMobile} deselectConversation={deselectConversation} businessWhatsappPhoneNumber={conversationData.LeadInformation.BusinessWhatsappPhoneNumber} businessInstagramUsername={conversationData.LeadInformation.BusinessInstagramUsername} assignedToUserId={conversationData.LeadInformation.AssignedToUserId} agentName={conversationData.AgentName} sequenceId={conversationData.LeadInformation.SequenceID ?? undefined} sequenceName={conversationData.LeadInformation.SequenceName ?? undefined} sequenceActive={conversationData.LeadInformation.SequenceActive ?? undefined} translation={{
      enabled: translation.enabled,
      isTranslating: translation.isTranslating,
      leadLanguageName: leadLanguageName || undefined,
      sameLanguage: translation.sameLanguage,
      onToggle: translation.toggle
    }} />

      <ConversationSummaryPanel leadId={conversationData.LeadInformation.LeadID} summary={conversationData.Summary} messageCount={messages.filter(message => !message.IsDeleted).length} isMobile={isMobile} />

      {/* The number this conversation sends through is disconnected at Meta, so
          nothing scheduled here will ever leave. Say it once, at the top, instead
          of letting the user discover it message by message. */}
      {conversationData.SendingBlocked && <div className={cn('flex flex-col gap-0.5 border-t border-border bg-red-50 px-4 py-2', isMobile && 'px-3')}>
          <span className="text-xs font-medium text-red-700">
            {conversationData.SendingBlocked.Reason === 'whatsapp_phone_paused' ? t('conversations.sendingBlockedPaused') : t('conversations.sendingBlockedDisconnected')}
          </span>
          {conversationData.SendingBlocked.PhoneNumber && <span className="text-xs text-red-600">
              {formatPhoneNumber(conversationData.SendingBlocked.PhoneNumber)}
            </span>}
        </div>}

      <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto py-4 ${isMobile ? 'px-3' : 'px-6'}`}>
        <div className="flex justify-center mb-6">
          <span className="text-xs text-foreground">
            {fDate(conversationData.LeadInformation.CreatedAt, 'MMMM d, yyyy')}
          </span>
        </div>

        <div className="flex flex-col gap-4">
          {timeline.map(item => {
          if (item.kind === 'event') {
            const obsolete = isEventObsolete(item.event);
            return <ConversationEventRow key={item.event.EventID} event={item.event} obsolete={obsolete} canNudge={item.event.EventID === nudgeableEventId && nudgeDisabledReason === null} nudgeDisabledReason={item.event.EventID === nudgeableEventId ? nudgeDisabledReason : null} isNudging={isTriggeringAi} hasNudged={hasTriggeredAi} onNudge={handleTriggerAiResponse} />;
          }
          const message = item.message;
          return <ChatMessage key={message.Timestamp} type={message.Inbound ? 'inbound' : 'outbound'} message={message.Content} messageId={message.MessageId} leadId={conversationData.LeadInformation.LeadID} sender={{
            name: message.Inbound ? conversationData.LeadInformation.FirstName : t('conversations.orsayAI'),
            id: message.Inbound ? conversationData.LeadInformation.LeadID : 'Orsay AI',
            profilePictureURL: message.Inbound ? conversationData.LeadInformation.ProfilePictureURL : undefined
          }} timestamp={message.Timestamp} isDeleted={message.IsDeleted} isInstagramConversation={conversationData.Channel === IChannel.INSTAGRAM} status={!message.Inbound ? message.Status : undefined} statusError={message.StatusError} feedback={message.Feedback} isComment={message.Type === ITriggerType.INSTAGRAM_COMMENT} isStoryReply={message.Type === ITriggerType.INSTAGRAM_STORY_REPLY} mediaUrl={message.Type === ITriggerType.INSTAGRAM_COMMENT ? conversationData.TriggerDetails?.MediaURL : message.Type === ITriggerType.INSTAGRAM_STORY_REPLY && conversationData.TriggerDetails?.MediaURL ? getStoryMediaUrl(conversationData.LeadInformation.LeadID) : undefined} externalUrl={message.Type === ITriggerType.INSTAGRAM_COMMENT ? conversationData.TriggerDetails?.ExternalURL : undefined} sentByAI={!message.Inbound && message.SentBy !== 'user'} buttons={message.Buttons} quickReplies={message.QuickReplies} isAudio={message.MediaType === 'audio'} audioUrl={message.AudioUrl} onResend={!message.Inbound && message.Status === MessageStatus.FAILED ? () => handleResend(message) : undefined} translation={message.IsDeleted ? undefined : translation.translationFor(message.Content)} />;
        })}

          {sortedUpcomingMessages.length > 0 && <>
              <MessagesSeparator />
              {sortedUpcomingMessages.map((message: MessageEntry) => <div key={message.Timestamp} className={cn('flex flex-col gap-4', message.Status === MessageStatus.FAILED ? 'opacity-100' : 'opacity-80')}>
                  <ChatMessage message={message.ContentUnavailable ? t('conversations.messageContentUnavailable') : message.Content} type={message.Inbound ? 'inbound' : 'outbound'} sender={{
              name: message.Inbound ? conversationData.LeadInformation.FirstName : t('conversations.orsayAI'),
              id: message.Inbound ? conversationData.LeadInformation.LeadID : 'Orsay AI',
              profilePictureURL: message.Inbound ? conversationData.LeadInformation.ProfilePictureURL : undefined
            }} timestamp={message.Timestamp} leadId={conversationData.LeadInformation.LeadID} isInstagramConversation={conversationData.Channel === IChannel.INSTAGRAM} status={message.Status} statusError={message.StatusError} sentByAI={!message.Inbound && message.SentBy !== 'user'} buttons={message.Buttons} quickReplies={message.QuickReplies} isUpcoming scheduledMessageId={message.ScheduledMessageID} pendingApproval={message.PendingApproval} translation={message.ContentUnavailable ? undefined : translation.translationFor(message.Content)} />
                </div>)}
            </>}
        </div>
      </div>

      {canTriggerAiResponse && (
    // The 24h messaging window is closed: the platform (WhatsApp/Instagram)
    // will reject any free-form reply, so the AI physically cannot respond.
    // Make that explicit instead of offering a trigger that silently fails.
    isChatInputDisabled ? <div className={cn('flex items-center gap-2 border-t border-border bg-amber-50 px-4 py-2', isMobile && 'px-3')}>
            <span className="flex-1 text-xs text-amber-700">
              {t('conversations.messagingWindowExpired')}
            </span>
          </div> : <div className={cn('flex items-center gap-2 border-t border-border bg-purple-50 px-4 py-2', isMobile && 'px-3')}>
            <span className="flex-1 text-xs text-purple-700">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(t as any)(isWaitingAiResponse ? 'conversations.waitingAiResponse' : 'conversations.aiDidNotRespond')}
            </span>
            <button onClick={handleTriggerAiResponse} disabled={isTriggeringAi || hasTriggeredAi} className="shrink-0 rounded-lg bg-purple-600 px-3 py-1 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors">
              {hasTriggeredAi ? t('conversations.aiResponseRequested') : isTriggeringAi ? '...' : t('conversations.triggerAiResponse')}
            </button>
          </div>)}

      <ChatInput conversationId={conversationData.LeadInformation.LeadID} placeholder={t('conversations.replyTo', {
      name: conversationData.LeadInformation.FirstName
    })} onSend={({
      text,
      clientMessageId
    }) => {
      handleSendMessage(text, clientMessageId);
    }} orgId={conversationData.LeadInformation.OrgId} leadId={conversationData.LeadInformation.LeadID} isAgentActive={conversationData.IsActive} keepAgentOnManualReply={conversationData.KeepAgentOnManualReply} isInstagramConversation={conversationData.Channel === IChannel.INSTAGRAM} isChatInputDisabled={isChatInputDisabled} translateReplies={translation.canTranslateReplies ? {
      targetLanguageName: leadLanguageName,
      onSent: translation.rememberTranslation
    } : undefined} />
    </div>;
};
export { ConversationChat };